import {useQuery} from '@apollo/client';
import React, {useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/system';
import {Dictionary, groupBy, mapValues, reverse, sortBy} from 'lodash';
import moment from 'moment';
import {Grid, Typography} from '@mui/material';
import parseHTML from 'html-react-parser';
import {useAccountContext} from '../../contexts/AccountContext';
import {PREVIOUS_HANDOVER_NOTES_QUERY} from '../../features/notes/notesQueries';
import {sortNotesByCategory} from '../../features/notes/sortNotesByCategory';
import {
  GetPreviousNotes,
  GetPreviousNotes_previousNotes,
  GetPreviousNotesVariables,
} from '../../schema-types/GetPreviousNotes';
import Loading from '../Loading/Loading';
import {HandoverNotesReviewDetailsFragment} from '../../schema-types/HandoverNotesReviewDetailsFragment';
import Theme from '../../theme/Theme';
import {Button} from '../Button/Button';
import validateUUID from '../../utils/validateUUID';

type reviewsByYear = Dictionary<HandoverNotesReviewDetailsFragment[]>;

export const AccountNotes: React.FC = () => {
  const {accountId, account} = useAccountContext();
  const [selectedReviewId, setSelectedReviewId] = useState<null | string>(null);
  const {data} = useQuery<GetPreviousNotes, GetPreviousNotesVariables>(
    PREVIOUS_HANDOVER_NOTES_QUERY,
    {
      variables: {
        accountId: validateUUID(accountId)!,
      },
      skip: !accountId,
    }
  );
  const reviewsByYear = useMemo(() => {
    if (!data?.previousNotes) {
      return [];
    }
    const results = groupBy(
      data.previousNotes.filter((review) => {
        const notes = review?.notes?.filter((note) => {
          return !!note.markupBody || !!note.body;
        });
        return !!notes?.length;
      }),
      (review) => {
        const date = review.dateBooked || review.dateScheduled || review.dateCreated;
        if (date) {
          return moment(date).get('year');
        }
        return 'no date';
      }
    );
    const mapped = Object.entries(
      mapValues(results, (reviews) => {
        return [...reviews].sort((review1, review2) => {
          const date1 =
            review1.dateBooked || review1.dateScheduled || review1.dateCreated;
          const date2 =
            review2.dateBooked || review2.dateScheduled || review1.dateCreated;
          if (date1 && date2) {
            return date2 - date1;
          }
          if (date1) {
            return -1;
          }
          if (date2) {
            return 1;
          }
          return 0;
        });
      })
    );
    return reverse(sortBy(mapped));
  }, [data]);
  useEffect(() => {
    if (reviewsByYear.length) {
      const latestYearReviews = reviewsByYear[0][1];
      setSelectedReviewId(latestYearReviews[0].id);
    }
  }, [reviewsByYear]);
  if (!data?.previousNotes) {
    return <Loading entityName="notes" />;
  }
  const selectedReview = data.previousNotes.find(
    (review) => review.id === selectedReviewId
  );
  const selectedReviewDate =
    selectedReview?.dateBooked ||
    selectedReview?.dateScheduled ||
    selectedReview?.dateCreated;
  if (!data.previousNotes.length) {
    return <Typography p={2}>No notes on account</Typography>;
  }
  return (
    <>
      <Grid p={2} container spacing={4}>
        <Grid item xs={3}>
          {reviewsByYear.map(([year, reviews]) => {
            return (
              <Box key={year}>
                <Typography mb={1} fontWeight="bold" color="secondary" variant="h4">
                  {year || 'No year found'}
                </Typography>
                <Box>
                  {reviews.map((review) => {
                    return (
                      <ReviewNotesMenuItem
                        key={review.id}
                        review={review}
                        selectedReviewId={selectedReviewId}
                        setSelectedReviewId={setSelectedReviewId}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Grid>
        <Grid item xs={9}>
          <Box sx={{display: 'flex', justifyContent: 'space-between'}} mb={3}>
            <Button
              variant="text"
              eventName={`View review ${selectedReview?.name}`}
              href={`/account/${accountId}/review-journey/${selectedReviewId}/notes`}
            >
              <Typography fontSize={26} fontWeight={600} color="primary">
                {selectedReview?.name}
              </Typography>
            </Button>
            <Typography textAlign="right" minWidth={100} whiteSpace="nowrap">
              <i>{moment(selectedReviewDate).format('MMM Do YYYY')}</i>
            </Typography>
          </Box>
          {sortNotesByCategory(
            selectedReview?.notes?.filter((note) => {
              return !!note.markupBody || !!note.body;
            }) ?? []
          ).map((note) => {
            return (
              <Box key={note.id} mb={4} mt={2}>
                <Typography fontSize={16} mb={1}>
                  <strong>{note.name}:</strong>
                </Typography>
                <Box>{note.markupBody ? parseHTML(note.markupBody) : note.body}</Box>
              </Box>
            );
          })}
        </Grid>
      </Grid>
    </>
  );
};

const ReviewNotesMenuItem: React.FC<{
  review: GetPreviousNotes_previousNotes;
  selectedReviewId: string | null;
  setSelectedReviewId: (val: string) => void;
}> = ({review, selectedReviewId, setSelectedReviewId}) => {
  const date = review.dateBooked || review.dateScheduled || review.dateCreated;
  const notes = review?.notes?.filter((note) => {
    return !!note.markupBody || !!note.body;
  });
  if (!notes?.length) {
    return null;
  }
  return (
    <Box
      sx={{
        borderRadius: 2,
        cursor: 'pointer',
        ...(review.id === selectedReviewId ? {background: Theme.palette.grey[200]} : {}),
        '&:hover': {
          background: Theme.palette.grey[100],
        },
      }}
      onClick={() => {
        setSelectedReviewId(review.id);
      }}
      key={review.id}
      p={2}
      mb={1}
    >
      <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
        <Typography fontSize={16} fontWeight={600} color="primary">
          {review.name}
        </Typography>
        <Typography textAlign="right" minWidth={100} whiteSpace="nowrap">
          <i>{moment(date).format('MMM Do')}</i>
        </Typography>
      </Box>
      <Box pt={1.5} pl={2}>
        {notes.map((note) => {
          return (
            <Box mb={1} key={note.id}>
              <Typography color="primary" fontSize={16}>
                {note.name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
